import { useEffect, useRef, useState } from "react";
import "./golfstatus-payments.scss";
import { Route, Routes, useLocation, useMatch } from "react-router-dom";
import {
  GSAppLayout,
  GSAppNavigationHeader,
  GSCircleImage,
  GSPageBanner,
  defaultLayouts,
  useMediaQuery,
  GSItemInfo,
} from "golfstatus_react_components";
import logo from "../images/GSLogo.svg";
import {
  faCartShopping,
  faCircleHalfStroke,
  faScrewdriverWrench,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  useNotificationBanner,
  useNotificationNavigation,
  useNotifications,
} from "../hooks/notificationHooks";
import {
  getCurrentAnnouncements,
  removeNotification,
  selectAnnouncements,
  selectLoggedInUser,
  selectNavItems,
  selectTheme,
  selectUser,
  setNotifications,
  setTheme,
} from "../reducers/appSlice";
import { getPublicTournamentDetails } from "../reducers/tournamentSlice";
import { useTheme } from "../hooks/themeHooks";
import { getOrderItemCount } from "../models/Registration";
import { getCurrentUser } from "../app/gs-session";
import {
  eventBaseRoute,
  getEventRoutes,
  getProp,
  TOURNAMENT_ID,
} from "../routes/event";
import { getInvoiceRoutes } from "../routes/invoice";
import { getEventListRoutes } from "../routes/eventList";
import { getRouting } from "../helpers/Converters";
import { usePermissions } from "../hooks/permissionHooks";
import { navigateToLogin } from "./events/event/cart/user-checkout-info";
import { useTournament } from "../hooks/tournamentHooks";
import GolfStatNotFound from "./golfstatus-not-found";
import {
  getRegistrationOrderThunk,
  selectRegistrationOrder,
} from "../reducers/registrationOrderSlice";
import { useGolfstatusNavigate } from "../hooks/navigationHooks";
import { defaultAvatar } from "./events/event/cart/customer-line-item";

const GolfstatusPayments = () => {
  const dispatch = useDispatch();
  const routeNavigate = useGolfstatusNavigate();
  const { pathname } = useLocation();

  const {
    appNotifications,
    tournamentNotifications,
    clearAllNotifications,
  } = useNotifications();

  const tournament = useTournament();
  const navItems = useSelector(selectNavItems);
  const registrationOrder = useSelector(selectRegistrationOrder);
  const loggedInUser = useSelector(selectLoggedInUser);
  const announcements = useSelector(selectAnnouncements);

  const registrationOrderID = registrationOrder?.id;

  const [hideEnvironment, setHideEnvironment] = useState(false);
  const [, theme, getStyle] = useTheme();
  const { getMaxZoom, isTablet, isPhone } = useMediaQuery();

  const permissions = usePermissions();

  const route = getRouting(eventBaseRoute, [TOURNAMENT_ID, "*"]);
  const match = useMatch(route);
  const id = getProp(match?.params, TOURNAMENT_ID);

  useNotificationNavigation(
    "rejected",
    tournamentNotifications,
    id ? `/event/${id}/not-found` : "/",
    "getPublicTournamentDetails",
  );

  useEffect(() => {
    if (id) {
      dispatch(getPublicTournamentDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (registrationOrderID && registrationOrderID !== "new-order") {
      dispatch(getRegistrationOrderThunk(registrationOrderID));
    }
  }, [dispatch, registrationOrderID]);

  useEffect(() => {
    dispatch(getCurrentAnnouncements());
    const user = getCurrentUser();
    if (user) {
      dispatch(selectUser(user?.uid));
    }
  }, [dispatch]);

  const announcementRef = useRef([]);
  const notificationRef = useRef([]);
  announcementRef.current = announcements;
  notificationRef.current = appNotifications;

  const totalAnnouncements = announcements.length;

  useEffect(() => {
    const notifications =
      announcementRef.current.map?.((announcement) => ({
        id: announcement.id,
        type: "announcement",
        state: "grey",
        header: (
          <GSItemInfo
            header={announcement.header}
            value={announcement.description}
            icon={faScrewdriverWrench}
          />
        ),
      })) ?? [];
    dispatch(
      setNotifications([
        ...notifications,
        ...notificationRef.current.filter?.((n) => n.type !== "announcement"),
      ]),
    );
  }, [dispatch, totalAnnouncements]);

  const getTitle = () => {
    return (
      <GSAppNavigationHeader
        logo={logo}
        title={tournament?.name ?? ""}
        onLogoClick={() => {
          routeNavigate("/");
        }}
        onTitleClick={() => {
          if (tournament?.publicUrl) {
            routeNavigate(getRouting(eventBaseRoute, [tournament?.publicUrl]));
          }
        }}
      ></GSAppNavigationHeader>
    );
  };

  const getFooter = () => {
    const defaultAvatar = "https://api.golfstatus.dev/images/user_round.png";
    return [
      {
        id: 0,
        label: (
          <GSCircleImage
            url={loggedInUser?.avatar ? loggedInUser.avatar : defaultAvatar}
            size="large"
          ></GSCircleImage>
        ),
        isSelectable: true,
        url: `${process.env.REACT_APP_GOLFSTATUS_PROFILE_URL}/account-settings`,
        isExternal: true,
      },
    ];
  };

  const navigate = (item) => {
    if (item.isExternal) {
      window.open(item?.url);
    } else {
      if (item?.url) {
        routeNavigate(item?.url);
      }
    }
  };

  const getEnvironmentActions = () => {
    return [
      {
        actionIcon: faTimesCircle,
        type: "navigation",
        size: "secondary",
        actionClick: hideNavEnvironment,
      },
    ];
  };

  const getShowEnvironment = () => {
    if (hideEnvironment) {
      return false;
    }
    return (
      process.env.REACT_APP_ENV !== "production" &&
      process.env.REACT_APP_ENV !== "demo"
    );
  };

  const hideNavEnvironment = () => {
    setHideEnvironment(true);
  };

  const getActiveRoute = () => {
    return `${pathname}`;
  };

  const getNav = () => {
    return {
      navItems: isPhone() || isTablet() ? navItems : [],
      title: getTitle(),
      footerItems: getFooter(),
      itemSelected: navigate,
      activeRoute: getActiveRoute(),
      showEnv: getShowEnvironment(),
      environmentActions: getEnvironmentActions(),
      environment: `${process.env.REACT_APP_ENV} environment`,
      environmentDescription: process.env.REACT_APP_GOLFSTATUS_BASE_API_URL,
      style: { ...getStyle(theme.outlineVariant) },
    };
  };

  const timeoutAction = () => {
    clearAllNotifications();
  };

  const getNavigationActions = () => {
    return [
      {
        title: "Clear",
        size: "secondary",
        onClick: timeoutAction,
      },
    ];
  };

  const announcementActions = (notification) => [
    {
      actionIcon: faTimesCircle,
      actionClick: () => {
        dispatch(removeNotification(notification));
      },
      type: "secondary transparent",
    },
  ];

  const getBannerActions = (notification) => {
    if (notification.type === "announcement") {
      return announcementActions(notification);
    }
    return notification.pageActions;
  };

  const [bannerNotifications] = useNotificationBanner({
    notifications: appNotifications?.map?.((notification) => ({
      ...notification,
      bannerActions: getBannerActions(notification),
    })),
    navigationActions: getNavigationActions(),
    timeoutAction,
  });

  const getRouter = () => {
    return (
      <Routes>
        {getEventListRoutes()}
        {getEventRoutes()}
        {getInvoiceRoutes()}
        <Route path="*" element={<GolfStatNotFound />}></Route>
      </Routes>
    );
  };

  const itemCount = getOrderItemCount(registrationOrder);
  const mode = useSelector(selectTheme);

  const getMobileActions = () => {
    const actions = [];

    const hideCart = match?.pathname?.includes?.("/leaderboards/tv");

    if (false) {
      actions.push({
        buttonIcon: faCircleHalfStroke,
        onClick: () => {
          dispatch(setTheme(mode === "light" ? "dark" : "light"));
        },
      });
    }

    if (!loggedInUser.id) {
      actions.unshift({
        title: "Sign In",
        type: "grey no-wrap",
        onClick: () => {
          navigateToLogin();
        },
      });
    } else {
      actions.unshift({
        style: { padding: 0, borderRadius: 50, height: "unset" },
        title: (
          <GSCircleImage
            url={loggedInUser?.avatar ? loggedInUser.avatar : defaultAvatar}
            size="large"
          />
        ),
        type: "transparent no-wrap",
        onClick: () => {
          window.open(process.env.REACT_APP_GOLFSTATUS_PROFILE_URL);
        },
      });
    }

    if (
      !hideCart &&
      tournament?.id &&
      registrationOrder?.id &&
      (permissions.packagesEnabled() || permissions.donationsActive())
    ) {
      actions.unshift({
        buttonIcon: faCartShopping,
        title: itemCount > 0 ? `${itemCount}` : "",
        onClick: () => {
          routeNavigate(
            getRouting(eventBaseRoute, [
              tournament?.publicUrl,
              "cart",
              registrationOrder?.id,
            ]),
          );
        },
      });
    }

    return actions;
  };

  return (
    <golfstatus-payments
      style={{ ...defaultLayouts.vStack, zoom: getMaxZoom(420) }}
    >
      <GSAppLayout
        nav={getNav()}
        navigationItemSelected={navigate}
        notification={
          appNotifications?.length > 0 ? (
            <GSPageBanner {...bannerNotifications} />
          ) : (
            false
          )
        }
        routes={getRouter()}
        navigation="top"
        mobileActions={getMobileActions()}
      ></GSAppLayout>
    </golfstatus-payments>
  );
};

export default GolfstatusPayments;
